// frontend/src/pages/Sabesp/DocumentViewer/index.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import { documentContentUrl, fetchDocumentPage, getCitation, getFigure, authHeader } from "../api/sabespClient.js";
import "./DocumentViewer.css";

// pdfjs-dist carregado via CDN (mesmo padrao ja usado pela Iris pros icones
// do Leaflet, em MapaVisualizer/index.js) em vez de dependencia local: o
// build da Iris roda num agente self-hosted que corrompe o worker .mjs do
// pdfjs-dist quando ele passa pelo pipeline webpack/Babel (imports absolutos
// da maquina de build injetados no arquivo minificado). Carregando a lib e o
// worker prontos da CDN, nenhum dos dois passa pelo bundler -- elimina o
// problema na raiz. `webpackIgnore` evita que o webpack tente resolver a URL
// como modulo local.
const PDFJS_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs";
const PDFJS_WORKER_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs";

let pdfjsLibPromise = null;
function loadPdfjs() {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import(/* webpackIgnore: true */ PDFJS_URL).then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return mod;
    });
  }
  return pdfjsLibPromise;
}

// Converte um retangulo normalizado (0..1) pra posicao na tela conforme a
// rotacao atual.
function position(rect, rotation) {
  const a = ((rotation % 360) + 360) % 360;
  if (a === 90) return { left: 1 - rect.y1, top: rect.x0, width: rect.y1 - rect.y0, height: rect.x1 - rect.x0 };
  if (a === 180) return { left: 1 - rect.x1, top: 1 - rect.y1, width: rect.x1 - rect.x0, height: rect.y1 - rect.y0 };
  if (a === 270) return { left: rect.y0, top: 1 - rect.x1, width: rect.y1 - rect.y0, height: rect.x1 - rect.x0 };
  return { left: rect.x0, top: rect.y0, width: rect.x1 - rect.x0, height: rect.y1 - rect.y0 };
}

// Paginas vizinhas (de cada lado) que vem junto com a pagina citada no recorte.
const WINDOW = 0; // so a pagina citada (as vizinhas encareciam a extracao sem necessidade)
const REFERENCE_GONE = "Esta referência não está mais disponível: o documento ou o grupo foi excluído.";

function friendlyError(err) {
  if (err && err.status === 404) return "Este documento não está mais disponível: ele ou o grupo foi excluído.";
  return `Falha ao carregar o PDF: ${(err && err.message) || String(err)}`;
}

export default function DocumentViewer({ documentId, page: initialPage, citationId, figureId, token, onClose }) {
  const targetPage = initialPage || 1;
  const [page, setPage] = useState(targetPage);
  // "window": so a pagina citada e as vizinhas (rapido, mesmo em contratos grandes); "full": documento inteiro.
  const [mode, setMode] = useState("window");
  const [windowed, setWindowed] = useState(true); // o PDF carregado e um recorte (nao o documento inteiro)
  const [windowStart, setWindowStart] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [referenceError, setReferenceError] = useState(null);
  const [scale, setScale] = useState(1.25);
  const [rotation, setRotation] = useState(0);
  const [citation, setCitation] = useState(null);
  const [figure, setFigure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [numPages, setNumPages] = useState(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const pdfDocRef = useRef(null);
  const loadedRef = useRef({ windowed: true, start: 1 });

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Trecho/figura da referencia (independente do PDF: se foi excluida, o leitor avisa em vez de ficar mudo).
  useEffect(() => {
    let cancelled = false;
    async function loadReference() {
      try {
        if (citationId) {
          const c = await getCitation(citationId, token);
          if (!cancelled) setCitation(c);
        }
        if (figureId) {
          const f = await getFigure(figureId, token);
          if (!cancelled) setFigure(f);
        }
      } catch (err) {
        if (!cancelled) setReferenceError(REFERENCE_GONE);
      }
    }
    loadReference();
    return () => {
      cancelled = true;
    };
  }, [citationId, figureId, token]);

  // PDF: primeiro so a pagina citada (recorte); "documento completo" so quando o usuario pedir.
  useEffect(() => {
    let cancelled = false;
    async function loadPdf() {
      try {
        const pdfjsLib = await loadPdfjs();
        let task;
        let start = 1;
        let total = null;
        let partial = mode === "window";
        if (partial) {
          try {
            const part = await fetchDocumentPage(documentId, targetPage, token, WINDOW);
            task = pdfjsLib.getDocument({ data: part.data });
            start = part.start;
            total = part.count;
          } catch (err) {
            if (err.status === 404) throw err;
            // Servidor sem o recorte de pagina: abre o documento inteiro, como antes.
            partial = false;
          }
        }
        if (!partial) {
          task = pdfjsLib.getDocument({ url: documentContentUrl(documentId), httpHeaders: authHeader(token) });
        }
        const pdfDoc = await task.promise;
        if (cancelled) {
          pdfDoc.destroy();
          return;
        }
        if (pdfDocRef.current) pdfDocRef.current.destroy();
        pdfDocRef.current = pdfDoc;
        loadedRef.current = { windowed: partial, start };
        setWindowed(partial);
        setWindowStart(start);
        setNumPages(pdfDoc.numPages);
        setTotalPages(partial ? total || null : pdfDoc.numPages);
        setPage((current) => (partial ? Math.min(Math.max(current, start), start + pdfDoc.numPages - 1) : current));
        setLoadError(null);
        setLoading(false);
      } catch (err) {
        if (!cancelled) setLoadError(friendlyError(err));
      }
    }
    loadPdf();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, mode, token]);

  useEffect(
    () => () => {
      if (pdfDocRef.current) pdfDocRef.current.destroy();
    },
    []
  );

  const drawHighlights = useCallback(
    (rot) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      wrap.querySelectorAll(".sabesp-dv-highlight").forEach((el) => el.remove());
      if (page !== targetPage) return;
      const rects = [];
      if (citation && Array.isArray(citation.rectangles)) {
        citation.rectangles.forEach((r) => rects.push({ r, type: "citation" }));
      }
      if (figure && figure.bbox) rects.push({ r: figure.bbox, type: "figure" });
      rects.forEach(({ r, type }) => {
        const p = position(r, rot);
        const el = document.createElement("span");
        el.className = "sabesp-dv-highlight" + (type === "figure" ? " figure" : "");
        el.style.left = p.left * 100 + "%";
        el.style.top = p.top * 100 + "%";
        el.style.width = p.width * 100 + "%";
        el.style.height = p.height * 100 + "%";
        wrap.appendChild(el);
      });
    },
    [citation, figure, page, targetPage]
  );

  const render = useCallback(async () => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!pdfDoc || !canvas || !wrap) return;
    const { windowed, start } = loadedRef.current;
    const pdfPage = await pdfDoc.getPage(windowed ? page - start + 1 : page);
    const viewport = pdfPage.getViewport({ scale, rotation });
    const ctx = canvas.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = viewport.width + "px";
    canvas.style.height = viewport.height + "px";
    wrap.style.width = viewport.width + "px";
    wrap.style.height = viewport.height + "px";
    await pdfPage.render({
      canvasContext: ctx,
      viewport,
      transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
    }).promise;
    requestAnimationFrame(() => drawHighlights(rotation));
  }, [page, scale, rotation, drawHighlights]);

  useEffect(() => {
    if (!loading) render();
  }, [loading, render]);

  function zoom(delta) {
    setScale((s) => Math.min(3, Math.max(0.6, s + delta)));
  }

  function rotate() {
    setRotation((r) => (r + 90) % 360);
  }

  const firstPage = windowed ? windowStart : 1;
  const lastPage = numPages ? (windowed ? windowStart + numPages - 1 : numPages) : page;
  const isPartial = windowed && totalPages && numPages && numPages < totalPages;

  function goTo(next) {
    setPage(Math.min(Math.max(next, firstPage), lastPage));
  }

  function openFullDocument() {
    setLoading(true);
    setMode("full");
  }

  function esc(s) {
    const d = document.createElement("div");
    d.innerText = s || "";
    return d.innerHTML;
  }

  return (
    <div className="sabesp-viewer-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sabesp-viewer-modal">
        <button className="sabesp-viewer-modal-close" title="Fechar (Esc)" onClick={onClose}>
          &times;
        </button>
        <div className="sabesp-dv-bar">
          <div className="sabesp-dv-doc-meta">
            <strong>{figure ? figure.name || "Documento" : citation ? citation.document_name || "Documento" : "Documento"}</strong>
            <span>
              Página {page}
              {totalPages ? ` de ${totalPages}` : ""}
            </span>
          </div>
          <div className="sabesp-dv-tools">
            <button onClick={() => zoom(-0.15)} title="Reduzir zoom">−</button>
            <span className="sabesp-dv-zoom">{Math.round(scale * 100)}%</span>
            <button onClick={() => zoom(0.15)} title="Aumentar zoom">+</button>
            <button onClick={rotate} title="Girar página">⟳</button>
            <button onClick={() => goTo(page - 1)} disabled={page <= firstPage} title="Página anterior">‹</button>
            <button onClick={() => goTo(page + 1)} disabled={page >= lastPage} title="Próxima página">›</button>
            {isPartial && (
              <button className="sabesp-dv-full-btn" onClick={openFullDocument} title="Carregar o documento inteiro">
                Documento completo
              </button>
            )}
          </div>
        </div>
        <div className="sabesp-dv-body">
          <div className="sabesp-dv-stage">
            <div className="sabesp-dv-page-wrap" ref={wrapRef}>
              <canvas ref={canvasRef}></canvas>
              {loading && <div className="sabesp-dv-loading">{loadError || "Carregando documento…"}</div>}
            </div>
          </div>
          <aside className="sabesp-dv-side">
            {referenceError ? (
              <p className="sabesp-dv-hint">{referenceError}</p>
            ) : figure ? (
              <>
                <span className="sabesp-dv-kicker">Figura relacionada</span>
                <h1>{figure.title || `Página ${page}`}</h1>
                <p className="sabesp-dv-description" dangerouslySetInnerHTML={{ __html: esc(figure.description) }} />
                <dl>
                  <div><dt>Tipo</dt><dd>{(figure.figure_type || "").replace(/_/g, " ")}</dd></div>
                  <div><dt>Localização</dt><dd>Página {figure.page_number}</dd></div>
                </dl>
                <p className="sabesp-dv-hint">O contorno verde enquadra a posição original da figura no documento.</p>
              </>
            ) : citation ? (
              <>
                <span className="sabesp-dv-kicker">Trecho utilizado</span>
                <h1>{citation.section_title || `Página ${page}`}</h1>
                <blockquote dangerouslySetInnerHTML={{ __html: esc(citation.quoted_text) }} />
                <dl>
                  <div><dt>Origem</dt><dd>{citation.source_type === "native_text" ? "Texto nativo" : "OCR"}</dd></div>
                  <div><dt>Localização</dt><dd>Página {citation.page_number}</dd></div>
                </dl>
                <p className="sabesp-dv-hint">As faixas amarelas indicam as linhas usadas na resposta e permanecem alinhadas durante o zoom.</p>
              </>
            ) : (
              <>
                <span className="sabesp-dv-kicker">Documento</span>
                <h1>Página {page}</h1>
                <p className="sabesp-dv-hint">O documento foi aberto na página correta.</p>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
